import { getProductDetailsQuery } from './queries';
import {
  PLUGIN_NAME,
  GTM_EVENTS,
  BLAZE_ECOMMERCE_EVENTS,
  LINE_ITEM_ACTION_TYPES
} from '../../constants';

const ecommerceEventHandler = ({ eventDetails, app, options = {}, windowObject = window }) => {
  const { eventName, client, cartData = {}, currency: eventCurrency } = eventDetails;
  const { currency: cartCurrency } = cartData || {};
  const currency = cartCurrency || eventCurrency;
  const {
    itemIdProperty = 'sku',
    brandProperty,
    categoryProperties = ['category.name'],
    dataLayerProperty = 'dataLayer',
    itemNameProperty = 'name'
  } = options;
  const GET_PRODUCT_DETAILS_QUERY = getProductDetailsQuery({
    brandProperty,
    categoryProperties,
    itemNameProperty
  });
  app.log.debug('plugin:ecommerce:event:*', eventDetails);

  const handleEvent = async () => {
    try {
      switch (eventName) {
        case BLAZE_ECOMMERCE_EVENTS.create_cart:
        case BLAZE_ECOMMERCE_EVENTS.add_to_cart:
          return await addToCart();
        case BLAZE_ECOMMERCE_EVENTS.purchase:
          return await genericEcommerceEvent();
        case BLAZE_ECOMMERCE_EVENTS.begin_checkout:
          return await genericEcommerceEvent(GTM_EVENTS.begin_checkout);
        case BLAZE_ECOMMERCE_EVENTS.view_item:
          return await viewItem();
        case BLAZE_ECOMMERCE_EVENTS.select_promotion:
          return await addDiscount();
        case BLAZE_ECOMMERCE_EVENTS.remove_from_cart:
          return await removeFromCart();
        case BLAZE_ECOMMERCE_EVENTS.update_cart:
          return await cartUpdate();
        default:
          return null;
      }
    } catch (error) {
      app.log.error('Error handling ecommerce event:', error);
    }
  };

  const cartUpdate = async () => {
    const { oldCartData: { lineItems: oldLineItems = [] } = {} } = eventDetails;
    const { lineItems: newLineItems = [] } = cartData || {};
    const changedItems = [];

    newLineItems.forEach(lineItem => {
      const foundItem = oldLineItems.find(
        item => item.productId === lineItem.productId && item.variantId === lineItem.variantId
      );
      if (!foundItem) {
        changedItems.push({ item: lineItem, action: LINE_ITEM_ACTION_TYPES.add });
      }
    });

    oldLineItems.forEach(lineItem => {
      const foundItem = newLineItems.find(
        item => item.productId === lineItem.productId && item.variantId === lineItem.variantId
      );
      if (!foundItem) {
        changedItems.push({ item: lineItem, action: LINE_ITEM_ACTION_TYPES.remove });
      }
    });

    newLineItems.forEach(lineItem => {
      const foundItem = oldLineItems.find(
        item => item.productId === lineItem.productId && item.variantId === lineItem.variantId
      );
      if (foundItem) {
        const difference = lineItem.quantity - foundItem.quantity;
        const action = difference > 0 ? LINE_ITEM_ACTION_TYPES.add : LINE_ITEM_ACTION_TYPES.remove;
        const quantity = Math.abs(difference);
        if (quantity <= 0) return;
        changedItems.push({
          item: { ...lineItem, quantity, originalQuantity: lineItem.quantity },
          action
        });
      }
    });

    const updates = { added: [], removed: [] };
    changedItems.forEach(({ item, action }) => {
      if (action === LINE_ITEM_ACTION_TYPES.add) {
        updates.added.push(item);
      } else if (action === LINE_ITEM_ACTION_TYPES.remove) {
        updates.removed.push(item);
      }
    });
    return Promise.all([
      sendAddToCartEvent(updates.added),
      sendRemoveFromCartEvent(updates.removed)
    ]);
  };

  // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#remove_from_cart
  const removeFromCart = async () => {
    const { input: [deletedItem] = [], oldCartData: { lineItems = [] } = {} } = eventDetails;

    if (!deletedItem) {
      return;
    }

    const deletedLineItem = lineItems.find(
      item => item.productId === deletedItem.productId && item.variantId === deletedItem.variantId
    );

    if (!deletedLineItem) {
      return;
    }

    return sendRemoveFromCartEvent([deletedLineItem]);
  };

  const sendRemoveFromCartEvent = async deletedLineItems => {
    if (!deletedLineItems || !deletedLineItems.length) return;

    const items = await Promise.all(deletedLineItems.map(getGtmItem));
    const value = Number(
      items
        .reduce((acc, item) => (item.price ? acc + item.price * item.quantity : acc), 0)
        .toFixed(2)
    );
    const removeFromCartEvent = {
      event: GTM_EVENTS.remove_from_cart,
      ecommerce: {
        currency,
        value,
        items
      }
    };

    updateDataLayer(removeFromCartEvent);
  };

  // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#select_promotion
  const addDiscount = () => {
    const { input } = eventDetails;
    const { discounts = [] } = cartData || {};
    const foundDiscount = discounts.find(discount => discount.code === input.code) || {};
    const event = {
      event: GTM_EVENTS.select_promotion,
      ecommerce: {
        coupon: input.code,
        promotion_id: foundDiscount.id,
        promotion_name: foundDiscount.name
      }
    };
    updateDataLayer(event);
  };

  // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#purchase
  // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#begin_checkout
  const genericEcommerceEvent = async (customEventName = GTM_EVENTS.purchase) => {
    const { result: order = {} } = eventDetails;
    const { lineItems = [], taxTotal, shippingTotal } = cartData || {};

    const discountCode = getDiscountCode();

    const items = await Promise.all(lineItems.map(getGtmItem));
    const value = items.reduce((acc, item) => acc + item.price * item.quantity, 0);

    const purchaseProperties =
      GTM_EVENTS.purchase === customEventName
        ? {
            tax: taxTotal,
            shipping: shippingTotal,
            transaction_id: order.id
          }
        : {};

    const event = {
      event: customEventName,
      ecommerce: {
        value,
        currency,
        coupon: discountCode,
        items,
        ...purchaseProperties
      }
    };
    updateDataLayer(event);
  };

  // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#view_item
  const viewItem = async () => {
    const { input: { selectedVariant = {}, parentId } = {} } = eventDetails;

    const quantity = selectedVariant.minQuantity || 1;
    const item = await getGtmItem({
      label: selectedVariant.name,
      ...selectedVariant,
      quantity,
      productId: parentId
    });
    const event = {
      event: GTM_EVENTS.view_item,
      ecommerce: {
        value: item.price ? item.price * quantity : null, // todo: check if this is correct value to use and not minus tax
        currency,
        items: [item]
      }
    };
    updateDataLayer(event);
  };

  // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#add_to_cart_item
  const addToCart = async () => {
    const { input: addedItems = [] } = eventDetails;
    const { lineItems = [] } = cartData || {};

    if (!lineItems || !lineItems.length || !addedItems || !addedItems.length) return;

    const addedLineItems = [];

    addedItems.forEach(item => {
      const existingItem = lineItems.find(
        lineItem => lineItem.productId === item.productId && lineItem.variantId === item.variantId
      );

      if (existingItem) {
        const price = getPriceNumber(existingItem.price);
        addedLineItems.push({ ...existingItem, quantity: item.quantity || 1, price });
      }
    });

    if (!addedLineItems || !addedLineItems.length) {
      return;
    }

    return sendAddToCartEvent(addedLineItems);
  };

  const sendAddToCartEvent = async addedLineItems => {
    if (!addedLineItems || !addedLineItems.length) return;

    const items = await Promise.all(addedLineItems.map(getGtmItem));
    const value = items.reduce((acc, item) => acc + item.price * item.quantity, 0);

    const gtmEvent = {
      event: GTM_EVENTS.add_to_cart,
      ecommerce: {
        currency,
        value,
        items
      }
    };

    return updateDataLayer(gtmEvent);
  };

  const getGtmItem = async (item, i) => {
    const { product, itemDetails } = await getProductDetails(item.productId);
    const quantity = item.quantity || 1;
    const gtmItem = {
      item_id: item[itemIdProperty] || item.sku || item.productId,
      item_name: (itemNameProperty ? product[itemNameProperty] : null) || item.name,
      // coupon: 'SUMMER_FUN', // todo: set item level discount code if available
      discount: getUnitValue(item.totalDiscount, quantity),
      index: i,
      item_variant: item.variantId,
      price: getUnitValue(item.totalPrice, item.originalQuantity || quantity),
      quantity,
      ...itemDetails
    };
    return gtmItem;
  };

  const getUnitValue = (value, quantity) => {
    const newTotal = getPriceNumber(value) / quantity;
    return newTotal ? Number(newTotal.toFixed(2)) : null;
  };

  const getPriceNumber = price => {
    if (typeof price !== 'string') return price;

    return parseFloat(price.replace(/[^0-9.,-]+/g, ''));
  };

  const getProductDetails = async productId => {
    const { data: { product = {} } = {} } = await client.query({
      query: GET_PRODUCT_DETAILS_QUERY,
      variables: { id: productId }
    });

    const itemDetails = {};

    if (brandProperty) {
      const brand = getPropertyFromQueryData(product, brandProperty);
      itemDetails.item_brand = Array.isArray(brand) ? brand.join(',') : brand;
    }

    if (categoryProperties && categoryProperties.length) {
      const categoryPropertiesData = categoryProperties.map(property =>
        getPropertyFromQueryData(product, property)
      );
      let counter = 0;
      categoryPropertiesData.forEach((categoryData, i) => {
        categoryData.forEach((category, j) => {
          counter += 1;
          const propertyName = `item_category${counter > 1 ? counter : ''}`;
          itemDetails[propertyName] = category;
        });
      });
    }

    return { itemDetails, product };
  };

  // todo: check format to pass total discount amount to GTM
  // const getDiscountAmount = value => value * -1;

  const getDiscountCode = () => {
    const { discounts = [] } = cartData || {};

    return discounts.map(discount => discount.code).join(', ');
  };

  const getPropertyFromQueryData = (data, property) => {
    if (!property) return [];

    if (!property.includes('.')) return data[property];

    const [parentProperty, childProperty] = property.split('.');
    if (!data[parentProperty]) return [];

    if (Array.isArray(data[parentProperty])) {
      return data[parentProperty].map(item => item[childProperty]);
    }
    return [data[parentProperty][childProperty]] || [];
  };

  const updateDataLayer = async eventData => {
    const { [dataLayerProperty]: dataLayer = [] } = windowObject;
    app.log.debug('blaze:gtm:dataLayer:update', eventData);
    await app.events.emitAsync(`plugin:${PLUGIN_NAME}:dataLayer:update`, eventData);
    dataLayer.push({ ecommerce: null });
    dataLayer.push(eventData);

    // eslint-disable-next-line no-param-reassign
    windowObject[dataLayerProperty] = dataLayer;
  };

  return handleEvent();
};

export default ecommerceEventHandler;
